// Plan de Emergencias v2. PE-C1: por capítulos (estructura de Kevin, src/lib/pe/pe-capitulos.ts): 8 capítulos con sus
// subapartados y avance, la pestaña opcional "Continuidad del negocio y póliza" y la fija "Revisión y aprobación"
// (resultados, plan de acción y versiones). Los campos y cálculos de los 6 pasos de antes (PE-A3a a PE-B2) se reubicaron
// sin cambios; lo que todavía no existe queda como "Próximamente". Vista previa en vivo en el orden del PDF.
// Diseño: docs/diseno/plan_emergencias_v2/PASO0_DISENO.md §4. La pantalla anterior (EmergencyPlanPage.tsx)
// sigue en su lugar. La pantalla obedece el rol, pero la seguridad la da la RLS (M32, M33).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCompany } from '../../CompanyContext';
import { useImpersonation } from '../../ImpersonationContext';
import { logAudit } from '../../auditLog';
import { syncPlanEmergenciasCompliance } from '../../lib/plan-emergencias-compliance-sync';
import type { ZonaSismica } from '../../lib/pe/pe-amenazas';
import {
  borrarAccion, borrarEvaluacion, borrarFilaCarga, borrarPlan, borrarSector, contarVersionesPlan, MENSAJE_PLAN_CON_VERSIONES, cargarAcciones, cargarBrigadaCapacitacion, cargarEvaluaciones, cargarFilasCarga, cargarPlan,
  cargarSectores, cargarTrabajadores, contarBrigada, guardarAccion, marcarOrigenResuelto, contarTrabajadoresActivos, FilaCargaEditable, FilaEvaluacion, FilaPlan, FuenteZona,
  guardarContinuidadYTransferencia, guardarDatosGenerales, guardarSector, Ocupacion, OCUPACIONES, rDeFila, RSector,
} from '../../lib/pe/pe-datos';
import type { CantonInec } from '../../lib/pe/pe-cantones';
import {
  ACTIVIDADES_RD164, CI_NOTA_COMPACTO, ciPorGrado, FUENTE_CI, FUENTE_R, MATERIALES_POR_MASA, MATERIALES_RD164, materialPorClave,
  NOTAS_T133, OPCIONES_R, permiteCiCompacto,
} from '../../lib/pe/pe-materiales-rd164';
import {
  AVISO_MAS_EXIGENTE, cantonPorCodigo, cantonPorNombre, capasNormativas, notaZona, zonaAlElegirCanton,
  zonaAlElegirPoblacion, ZonaPropuesta, ZONAS_SISMICAS,
} from '../../lib/pe/pe-paso1';
import { CANTONES_INEC } from '../../lib/pe/pe-cantones';
import {
  AVISO_CONTINUIDAD, CAPITULOS, capitulo, etiquetaVista, IdVista, NumCapitulo, PROXIMAMENTE, resumenProgreso, SIN_REQUISITOS, tieneRequisitos,
  SUB_REVISION, SubRevision, textoConCapitulos, tieneContinuidad, TITULO_CONTINUIDAD_PESTANA, TITULO_REVISION, vistaDePaso,
} from '../../lib/pe/pe-capitulos';
import { dondeSeCompleta, pendientes } from '../../lib/pe/pe-plan-pdf';
import { armarVistaPrevia, DatosVista } from '../../lib/pe/pe-vista-previa';
import SelectBuscable from '../../components/SelectBuscable';
import { plural } from '../../lib/plurales';
import VistaPreviaCapitulos from './VistaPreviaCapitulos';
import {
  Calculo, calcularGrupos, calcularSector, ETIQUETA_ART139, ETIQUETA_OCUPACION, guardarCargaFuego, OCUPACIONES_SECTOR,
  SectorEnPantalla,
} from '../../lib/pe/pe-paso2';
import { etiquetaPoblacion, poblacionesPorCanton, poblacionPorEtiqueta, zonaPorCanton } from '../../lib/pe/pe-zonas-sismicas';
import { formatoEc, leerDecimalEc } from '../../lib/pe/pe-unidades';
import type { Edificacion } from '../../lib/pe/pe-evacuacion';
import { calcularPaso3, ContextoPaso3, entradasVacias, EntradasPaso3, unidadesMeseri } from '../../lib/pe/pe-paso3';
import { entradasDesdeFilas, guardarPaso3, limpiarMeseriHuerfanas, meseriHuerfanas, resultadosCambiados } from '../../lib/pe/pe-paso3-guardado';
import { ModalConfirmar } from './ComponentesPE';
import Paso1Continuidad from './Paso1Continuidad';
import Paso2Inventario from './Paso2Inventario';
import { cargarVersiones, estadoDelPlan, FilaVersion, versionVigente } from '../../lib/pe/pe-versiones';
import {
  Bodega, cargarBodegas, cargarEnlaces, cargarInventarioDe, EnlaceSector, enlazarSector, nombreMaterial, PropuestaCarga, quitarEnlace,
} from '../../lib/pe/pe-inventario';
import type { Continuidad, Transferencia } from '../../lib/pe/pe-continuidad';
import Paso3Calculos from './Paso3Calculos';
import Paso4Resultados from './Paso4Resultados';
import Paso5Acciones from './Paso5Acciones';
import Paso6Versiones from './Paso6Versiones';
import { AccionEditable, FilaAccion, sincronizarAcciones } from '../../lib/pe/pe-acciones';
import { borrarEvidencia, cargarEvidencias, FilaEvidencia, subirEvidencia, urlEvidencia } from '../../lib/pe/pe-evidencias';
import type { PlanEvacuacion } from '../../lib/pe/pe-simulacros';
import Simulacros from './Simulacros';
import { useSedes } from '../../lib/useSedes';
import { avisoCantonPlan, matrizDe } from '../../lib/sedes';
import './PlanEmergenciasV2.css';

// Orden de la navegación: los 8 capítulos, la continuidad (opcional) y la revisión.
const ORDEN: IdVista[] = [1, 2, 3, 4, 5, 6, 7, 8, 'continuidad', 'revision'];
interface Destino { vista: IdVista; sub?: SubRevision }
// PE-C1: el capítulo abierto se recuerda en la sesión del navegador (por empresa), no en la base.
const CLAVE_VISTA = (companyId: string) => `pe2-vista:${companyId}`;
function vistaGuardada(companyId: string): Destino {
  try {
    const d = JSON.parse(sessionStorage.getItem(CLAVE_VISTA(companyId)) ?? 'null') as Destino | null;
    if (d && ORDEN.includes(d.vista)) return d;
  } catch { /* sin sessionStorage: capítulo 1 */ }
  return { vista: 1 };
}
const SIN_PLAN = (ro: boolean) => (ro ? 'Todavía no hay un plan para esta empresa.' : 'Guarda primero los datos generales (capítulo 1).');

interface Confirmacion { titulo: string; mensaje: string; accion: () => Promise<void>; escribir?: string; textoConfirmar?: string; bloqueado?: string }

// Texto que hay que escribir para borrar el plan: su código; sin código, la palabra BORRAR (PE-A3c).
export const textoParaBorrarPlan = (codigo: string | null | undefined) => codigo?.trim() || 'BORRAR';

// Número → texto editable con coma decimal (sin miles).
const aTexto = (n: number | null | undefined) => (n === null || n === undefined ? '' : String(n).replace('.', ','));
const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));

// ── Borradores de la pantalla (texto en los campos numéricos para aceptar la coma) ──

type ModoQ = 'tabla' | 'manual';
type ModoCi = 'tabla_fila' | 'grado' | 'manual';

interface FilaBorrador {
  id?: string;
  modo: ModoQ;
  tableRef: string;
  material: string;
  qi: string;
  qFuente: string;
  ciModo: ModoCi;
  ciGrado: number;
  ciHumo: boolean;
  ci: string;
  ciFuente: string;
  ciCompacto: boolean;     // Ci = 1 por la nota 5 o 6 de la Tabla 1.3.3 (con justificación)
  ciJustificacion: string;
  kg: string;
  productId?: string | null; // PE-A4: fila propuesta desde el inventario
  origen?: string | null;    // emergency_fire_load_items.origin_note (M37)
}

interface SectorBorrador {
  id?: string;
  sortOrder: number;
  nombre: string;
  area: string;
  ocupantes: string;  // emergency_plan_sectors.occupants (RTQ 5, 5.2.2)
  rociadores: boolean; // emergency_plan_sectors.has_sprinklers (recorrido de la RTQ 3)
  actividad: Ocupacion;
  cortafuego: boolean;
  grupo: string;
  rSel: string; // '0.8' | '1' | '1.4' | '1.8' | 'manual'
  rManual: string;
  rFuente: string;
  filas: FilaBorrador[];
}

const filaNueva = (): FilaBorrador => ({
  modo: 'tabla', tableRef: MATERIALES_POR_MASA[0].clave, material: '', qi: '', qFuente: '', ciModo: 'tabla_fila', ciGrado: 3,
  ciHumo: false, ci: '', ciFuente: '', ciCompacto: false, ciJustificacion: '', kg: '',
});

function filaABorrador(f: FilaCargaEditable): FilaBorrador {
  const mat = f.q_source !== 'manual' ? materialPorClave(f.table_ref) : undefined;
  const filaTabla = !!mat && f.ci_origin === 'tabla' && f.ci_grade === null;
  const compacto = filaTabla && !!mat && permiteCiCompacto(mat) && f.ci === CI_NOTA_COMPACTO && mat.ci !== CI_NOTA_COMPACTO;
  return {
    id: f.id, modo: mat ? 'tabla' : 'manual', tableRef: f.table_ref ?? '', material: f.material, qi: aTexto(f.q_value),
    qFuente: f.q_source_note ?? '',
    ciModo: f.ci_origin === 'manual' ? 'manual' : filaTabla ? 'tabla_fila' : 'grado',
    ciGrado: f.ci_grade ?? 3, ciHumo: f.ci_smoke, ci: aTexto(f.ci), ciFuente: f.ci_origin === 'manual' ? f.ci_source ?? '' : '',
    ciCompacto: compacto, ciJustificacion: compacto ? f.ci_source ?? '' : '', kg: aTexto(f.mass_kg),
    productId: f.product_id, origen: f.origin_note ?? null,
  };
}

// PE-A4: propuesta del inventario → fila del borrador. Con material del RD 164, la fila de la tabla con su nombre; sin
// él, una fila manual con el nombre del producto (el responsable lo cambia si quiere e ingresa qi con su fuente). Sin
// kg, la masa queda vacía (por validar).
function propuestaABorrador(p: PropuestaCarga): FilaBorrador {
  const base = { ...filaNueva(), kg: p.masaKg !== null ? aTexto(p.masaKg) : '', productId: p.productId, origen: p.origen };
  return p.material
    ? { ...base, modo: 'tabla', tableRef: p.material.clave, material: nombreMaterial(p.material) }
    : { ...base, modo: 'manual', material: p.producto, ciModo: 'grado' };
}

// Borrador → fila de M33. Los números que no se pueden leer quedan en NaN y el motor o la validación avisan.
function borradorAFila(b: FilaBorrador): FilaCargaEditable {
  const mat = b.modo === 'tabla' ? materialPorClave(b.tableRef) : undefined;
  let ci: number; let ci_origin: 'tabla' | 'manual' = 'tabla'; let ci_grade: number | null = null; let ci_smoke = false;
  let ci_source: string | null = null;
  if (mat && b.ciModo === 'tabla_fila') {
    const compacto = b.ciCompacto && permiteCiCompacto(mat);
    ci = compacto ? CI_NOTA_COMPACTO : mat.ci;
    ci_source = compacto ? b.ciJustificacion : null;
  } else if (b.ciModo === 'manual') { ci = leerDecimalEc(b.ci) ?? NaN; ci_origin = 'manual'; ci_source = b.ciFuente; }
  else { ci = ciPorGrado(b.ciGrado, b.ciHumo); ci_grade = b.ciGrado; ci_smoke = b.ciHumo; }
  return {
    id: b.id, sort_order: 0, method: 'materiales',
    material: mat ? mat.nombre : b.material,
    table_ref: mat ? mat.clave : null,
    q_value: mat ? mat.qi : leerDecimalEc(b.qi) ?? NaN,
    q_unit: 'MJ/kg',
    q_source: mat ? mat.tabla : 'manual',
    q_source_note: mat ? null : b.qFuente,
    mass_kg: leerDecimalEc(b.kg) ?? NaN,
    surface_m2: null, height_m: null,
    ci, ci_origin, ci_source, ci_grade, ci_smoke, product_id: b.productId ?? null, origin_note: b.origen ?? null,
  };
}

function rDeBorrador(b: SectorBorrador): RSector {
  if (b.rSel === 'manual') return { r: leerDecimalEc(b.rManual) ?? NaN, rOrigen: 'manual', rFuente: b.rFuente };
  return { r: Number(b.rSel), rOrigen: 'tabla', rFuente: null };
}

function borradorAPantalla(b: SectorBorrador): SectorEnPantalla {
  const r = rDeBorrador(b);
  return {
    sector: { id: b.id, name: b.nombre, occupancy: b.actividad, area_m2: leerDecimalEc(b.area) ?? NaN, occupants: leerDecimalEc(b.ocupantes), has_sprinklers: b.rociadores,
      fire_separated: b.cortafuego, group_key: b.grupo || null, sort_order: b.sortOrder,
      r_value: r.r, r_origin: r.rOrigen, r_source: r.rOrigen === 'manual' ? r.rFuente : null },
    filas: b.filas.map(borradorAFila),
    r,
  };
}

function pantallaABorrador(s: SectorEnPantalla): SectorBorrador {
  const enTabla = s.r.rOrigen === 'tabla' && OPCIONES_R.some(o => o.valor === s.r.r);
  return {
    id: s.sector.id, sortOrder: s.sector.sort_order, nombre: s.sector.name, area: aTexto(s.sector.area_m2), ocupantes: aTexto(s.sector.occupants), rociadores: s.sector.has_sprinklers ?? false,
    actividad: s.sector.occupancy, cortafuego: s.sector.fire_separated, grupo: s.sector.group_key ?? '',
    rSel: enTabla ? String(s.r.r) : 'manual', rManual: enTabla ? '' : aTexto(s.r.r), rFuente: s.r.rFuente ?? '',
    filas: s.filas.map(filaABorrador),
  };
}

// ── Piezas pequeñas ──────────────────────────────────────────────────────────

function Campo({ etiqueta, children, nota }: { etiqueta: string; children: React.ReactNode; nota?: React.ReactNode }) {
  return <label className="pe2-campo"><span>{etiqueta}</span>{children}{nota && <small>{nota}</small>}</label>;
}

function Numero({ valor, onChange, unidad, disabled, entero }: {
  valor: string; onChange: (v: string) => void; unidad: string; disabled?: boolean; entero?: boolean;
}) {
  return (
    <div className="pe2-unidad">
      <input type="text" inputMode={entero ? 'numeric' : 'decimal'} value={valor} disabled={disabled}
        onChange={e => onChange(e.target.value)} />
      <em>{unidad}</em>
    </div>
  );
}

function Resultado({ calculo }: { calculo: Calculo }) {
  if (!calculo.ok) return <div className="pe2-aviso">No se puede calcular todavía: {calculo.error}</div>;
  const r = calculo.resultado;
  const porValidar = r.trazas.filter(t => t.estado === 'por_validar');
  return (
    <>
      <div className="pe2-res">
        <div><b>{formatoEc(r.qsMJm2, 1)} MJ/m²</b><span>Qs (RD 164, Anexo I 3.2.1)</span></div>
        <div><b>{formatoEc(r.qsKcalm2, 0)} kcal/m²</b><span>Qs en kcal/m²</span></div>
        <div><b>Nivel {r.nivelRD164.nivel} ({r.nivelRD164.categoria})</b><span>RD 164, Tabla 1.3.1 (dato técnico)</span></div>
        <div><b>{ETIQUETA_ART139[r.clasificacionArt139]}</b><span>AM 1257, Art. 139 (clasificación legal)</span></div>
      </div>
      {porValidar.length > 0 && (
        <div className="pe2-aviso">
          Valores fuera de las tablas del RD 164 (aparecerán con aviso en el PDF):
          <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{porValidar.map((t, i) => <li key={i}>{t.nota}</li>)}</ul>
        </div>
      )}
    </>
  );
}

// Subapartado de un capítulo (PE-C1). A nivel de módulo: si se definiera dentro del render, React remontaría sus campos
// en cada tecla y se perdería el foco.
function Sub({ id, numero, titulo, proximamente, children }: { id: string; numero: string; titulo: string; proximamente?: string; children?: React.ReactNode }) {
  return (
    <section className="pe2-card pe2-sub" data-sub={id} aria-label={`${numero} ${titulo}`}>
      <h3>{numero} {titulo}</h3>
      {proximamente && <div className="pe2-proximamente" role="note"><b>{PROXIMAMENTE}.</b> {proximamente}</div>}
      {children}
    </section>
  );
}
function subDe(n: NumCapitulo, id: string) {
  const c = capitulo(n); const i = c.subapartados.findIndex(s => s.id === id);
  return { id, numero: `${n}.${i + 1}`, titulo: c.subapartados[i].titulo, proximamente: c.subapartados[i].proximamente };
}

// ── Pantalla ─────────────────────────────────────────────────────────────────

export default function PlanEmergenciasV2() {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const companyId = (impersonated ?? company)?.id ?? '';
  // Igual que EmergencyPlanPage y la RLS de M33: viewer solo lee; manager crea y edita; admin además borra.
  const puedeEditar = profile?.role === 'admin' || profile?.role === 'manager' || !!impersonated;
  const puedeBorrar = profile?.role === 'admin' || !!impersonated;
  const usuarioId = profile?.id ?? null;

  // PE-C1: capítulo (o pestaña) abierto y, en la revisión, su parte. Los simulacros (PE-B2) están en el capítulo 7.
  const [{ vista, sub: subRev = 'resultados' }, setDestino] = useState<Destino>(() => vistaGuardada(companyId));
  const [navAbierta, setNavAbierta] = useState(false); // lista de capítulos en el celular
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [guardando, setGuardando] = useState(false);

  const [plan, setPlan] = useState<FilaPlan | null>(null);
  const [sectores, setSectores] = useState<SectorEnPantalla[]>([]);
  const [sugeridos, setSugeridos] = useState<number | null>(null);

  // Paso 1
  const [codigo, setCodigo] = useState('');
  const [canton, setCanton] = useState<CantonInec | undefined>(undefined);
  const [area, setArea] = useState('');
  const [ocupacion, setOcupacion] = useState<Ocupacion | ''>('');
  const [trabajadores, setTrabajadores] = useState('');
  const [zona, setZona] = useState<ZonaSismica | ''>('');
  const [zonaFuente, setZonaFuente] = useState<FuenteZona>('manual');
  const [lugar, setLugar] = useState<string | null>(null); // seismic_place
  const [km, setKm] = useState('');
  const [minutos, setMinutos] = useState('');
  const [edificacion, setEdificacion] = useState<Edificacion | ''>(''); // site_is_new (PE-36)
  const nec = useMemo(() => (canton ? zonaPorCanton(canton.codigo) : null), [canton]);
  const poblaciones = useMemo(() => poblacionesPorCanton(canton?.codigo), [canton]);
  const elegida = zonaFuente === 'nec_tabla19' ? poblacionPorEtiqueta(canton?.codigo, lugar) : undefined;
  // F1a (M40): sede del plan, solo lectura. Sin plan guardado, el plan nuevo irá a la Matriz. No cambia ningún cálculo.
  const { sedes } = useSedes(companyId);
  const sedePlan = sedes.find(s => s.id === plan?.site_id) ?? (plan ? null : matrizDe(sedes));
  const avisoCanton = avisoCantonPlan(sedePlan?.canton_code, canton?.codigo);

  // Paso 2
  const [edicion, setEdicion] = useState<SectorBorrador | null>(null);

  // Pasos 3 y 4 (PE-A3b)
  const [entradas3, setEntradas3] = useState<EntradasPaso3>(entradasVacias);
  const [filasEval, setFilasEval] = useState<FilaEvaluacion[]>([]);
  const [brigadaMiembros, setBrigadaMiembros] = useState<number | null>(null);
  const [brigadaFilas, setBrigadaFilas] = useState<{ workerId: string; trained: boolean; trainingDate: string | null }[] | null>(null);
  const [confirmar, setConfirmar] = useState<Confirmacion | null>(null);
  // Paso 5 (PE-A3b): si la base aún no tiene M35, el plan de acción no se puede leer; el resto sigue funcionando.
  const [acciones, setAcciones] = useState<FilaAccion[]>([]);
  const [errorAcciones, setErrorAcciones] = useState('');
  // PE-A4: enlaces sector ↔ bodega o zona (M37). Sin M37 en la base, el paso 2 sigue funcionando y lo avisa.
  const [bodegas, setBodegas] = useState<Bodega[]>([]);
  const [enlaces, setEnlaces] = useState<EnlaceSector[]>([]);
  const [errorInventario, setErrorInventario] = useState('');
  // PE-A4: evidencias por acción (M37). Sin M37 en la base, el paso 5 sigue funcionando y lo avisa.
  const [evidencias, setEvidencias] = useState<FilaEvidencia[]>([]);
  const [errorEvidencias, setErrorEvidencias] = useState('');
  const [trabajadoresLista, setTrabajadoresLista] = useState<{ id: string; full_name: string }[]>([]);
  const [borrando, setBorrando] = useState(false);
  // PE-A4b: versión vigente (con su foto) para el estado del plan del encabezado.
  const [vigente, setVigente] = useState<FilaVersion | null>(null);

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setCargando(true); setError('');
    try {
      const [p, n] = await Promise.all([cargarPlan(companyId), contarTrabajadoresActivos(companyId).catch(() => null)]);
      setPlan(p); setSugeridos(n);
      const secs = p ? await cargarSectores(p.id) : [];
      const filas = await cargarFilasCarga(secs.map(s => s.id));
      const secsPantalla = secs.map(s => ({ sector: s, filas: filas.filter(f => f.sector_id === s.id), r: rDeFila(s) }));
      setSectores(secsPantalla);
      try {
        setBodegas(await cargarBodegas(companyId));
        setEnlaces(await cargarEnlaces(secs.map(s => s.id)));
        setErrorInventario('');
      } catch (err) {
        console.error('cargarEnlaces', err);
        setEnlaces([]); setErrorInventario(msg(err));
      }
      // Paso 3: entradas guardadas; si faltan, se prellenan con los parámetros del responsable del plan (M33).
      const evs = p ? await cargarEvaluaciones(p.id) : [];
      setFilasEval(evs);
      // Solo cuentan las filas MESERI de los encabezados actuales (sector suelto o primer sector del grupo).
      const cabezas = new Set(unidadesMeseri(secsPantalla).map(u => u.sectorId).filter((x): x is string => !!x));
      const e3 = entradasDesdeFilas(evs, cabezas);
      if (p) {
        e3.punto.m2PorPersona = e3.punto.m2PorPersona ?? p.assembly_m2_per_person;
        e3.punto.distanciaMinimaM = e3.punto.distanciaMinimaM ?? p.assembly_min_distance_m;
        e3.evacuacion.premovimientoS = e3.evacuacion.premovimientoS ?? p.premovement_s;
      }
      setEntradas3(e3);
      setBrigadaMiembros(await contarBrigada(companyId));
      setBrigadaFilas(await cargarBrigadaCapacitacion(companyId));
      setTrabajadoresLista(await cargarTrabajadores(companyId).catch(err => { console.error('cargarTrabajadores', err); return []; }));
      if (p) {
        try { setAcciones(await cargarAcciones(p.id)); setErrorAcciones(''); } catch (err) {
          console.error('cargarAcciones', err);
          setAcciones([]); setErrorAcciones(msg(err));
        }
        await recargarEvidencias(p.id);
      }
      setVigente(p ? versionVigente(await cargarVersiones(p.id).catch(err => { console.error('cargarVersiones', err); return []; })) : null);
      // Paso 1 desde lo guardado (antes de M34 solo había el nombre del cantón en site_canton)
      setCodigo(p?.code ?? '');
      const c = cantonPorCodigo(p?.canton_code) ?? cantonPorNombre(p?.site_canton);
      setCanton(c);
      setArea(aTexto(p?.site_area_m2));
      setOcupacion(p?.site_occupancy ?? '');
      setTrabajadores(p?.site_workers != null ? String(p.site_workers) : n != null ? String(n) : '');
      setZona(p?.seismic_zone ?? '');
      setZonaFuente(p?.seismic_zone_source ?? 'manual');
      setLugar(p?.seismic_place ?? null);
      if (p && !p.seismic_zone && c) {
        const z = zonaAlElegirCanton(c);
        setZona(z.zona ?? ''); setZonaFuente(z.fuente); setLugar(z.lugar);
      }
      setKm(aTexto(p?.fire_station_km)); setMinutos(aTexto(p?.fire_station_min));
      setEdificacion(p?.site_is_new === true ? 'nueva' : p?.site_is_new === false ? 'existente' : '');
    } catch (e) {
      setError(msg(e));
    } finally {
      setCargando(false);
    }
  }, [companyId]);

  useEffect(() => { cargar(); }, [cargar]);

  // Decisión de JC (27/10/2026): elegir otra población de la Tabla 19 lo puede hacer quien edita (gestor o
  // administrador); cambiar la zona a mano cuando el cantón está en la Tabla 19, solo el administrador. Sin
  // dato de la NEC, la zona la elige quien edita.
  const puedeCambiarZona = puedeEditar && (!nec || puedeBorrar);

  function aplicarZona(z: ZonaPropuesta) {
    setZona(z.zona ?? ''); setZonaFuente(z.fuente); setLugar(z.lugar);
  }

  function elegirCanton(c: CantonInec) {
    setCanton(c);
    aplicarZona(zonaAlElegirCanton(c));
  }

  function elegirPoblacion(etiqueta: string) {
    const p = poblacionPorEtiqueta(canton?.codigo, etiqueta);
    if (p) aplicarZona(zonaAlElegirPoblacion(p));
  }

  // Ajuste manual (solo administrador si el cantón está en la Tabla 19): la zona queda "manual", sin población.
  function cambiarZona(z: ZonaSismica | '') {
    if (elegida && z === elegida.zona) return;
    setZona(z); setZonaFuente('manual'); setLugar(null);
  }

  async function guardarPaso1(): Promise<boolean> {
    setError(''); setOk('');
    const a = leerDecimalEc(area); const t = leerDecimalEc(trabajadores);
    const k = leerDecimalEc(km); const m = leerDecimalEc(minutos);
    if ([a, t, k, m].some(v => v !== null && Number.isNaN(v))) { setError('Revisa los números: usa coma decimal (por ejemplo 12,5).'); return false; }
    setGuardando(true);
    try {
      await guardarDatosGenerales(companyId, {
        code: codigo, canton_code: canton?.codigo ?? null, site_canton: canton?.canton ?? null, site_province: canton?.provincia ?? null,
        site_area_m2: a, site_occupancy: ocupacion || null, site_workers: t,
        seismic_zone: zona || null, seismic_zone_source: zona ? zonaFuente : null, seismic_place: lugar,
        fire_station_km: k, fire_station_min: m,
        site_is_new: edificacion === 'nueva' ? true : edificacion === 'existente' ? false : null,
      }, usuarioId, plan);
      setOk('Datos generales guardados (borrador).');
      await cargar();
      return true;
    } catch (e) {
      setError(msg(e)); return false;
    } finally {
      setGuardando(false);
    }
  }

  // Sectores con el que se está editando en su lugar: así el cálculo y la agrupación se ven en vivo.
  const enVivo = useMemo<SectorEnPantalla[]>(() => {
    if (!edicion) return sectores;
    const b = borradorAPantalla(edicion);
    return edicion.id ? sectores.map(s => (s.sector.id === edicion.id ? b : s)) : sectores.concat(b);
  }, [sectores, edicion]);
  const grupos = useMemo(() => calcularGrupos(enVivo), [enVivo]);
  const calculoEdicion = useMemo(() => (edicion ? calcularSector(borradorAPantalla(edicion)) : null), [edicion]);

  async function guardarEdicion() {
    if (!edicion || !plan) return;
    setError(''); setOk(''); setGuardando(true);
    try {
      const b = borradorAPantalla(edicion);
      if (Number.isNaN(b.sector.area_m2) || b.filas.some(f => Number.isNaN(f.q_value) || Number.isNaN(f.ci) || Number.isNaN(f.mass_kg as number))) {
        throw new Error('Revisa los números del sector y de sus filas: usa coma decimal (por ejemplo 12,5).');
      }
      if (b.r.rOrigen === 'manual' && (!(b.r.r > 0) || !b.r.rFuente?.trim())) throw new Error('R manual necesita un valor mayor que 0 y su fuente.');
      const { sector, filas } = await guardarSector(companyId, plan.id, b.sector, b.filas, usuarioId);
      const guardado: SectorEnPantalla = { sector, filas, r: rDeFila(sector) };
      const todos = (edicion.id ? sectores.map(s => (s.sector.id === sector.id ? guardado : s)) : sectores.concat(guardado));
      await guardarCargaFuego(companyId, plan.id, todos.map(s => ({ ...s, sector: { ...s.sector, id: s.sector.id as string } })), usuarioId);
      setEdicion(null);
      setOk(`Sector "${sector.name}" guardado.`);
      await cargar();
    } catch (e) {
      setError(msg(e));
    } finally {
      setGuardando(false);
    }
  }

  // Borrar sector: modal propio (nunca confirm del navegador).
  function borrarSectorConfirmado(s: SectorEnPantalla) {
    if (!s.sector.id || !plan) return;
    const sectorId = s.sector.id;
    const planId = plan.id;
    setConfirmar({
      titulo: 'Borrar sector',
      mensaje: `Se borrará el sector y sus filas de carga de fuego ("${s.sector.name}").`,
      accion: async () => {
        await borrarSector(sectorId);
        const resto = sectores.filter(x => x.sector.id !== sectorId);
        await guardarCargaFuego(companyId, planId, resto.map(x => ({ ...x, sector: { ...x.sector, id: x.sector.id as string } })), usuarioId);
        setOk(`Sector "${s.sector.name}" borrado.`);
        await cargar();
      },
    });
  }

  // Quitar una fila: si ya está guardada, se borra (solo admin) con el modal propio.
  function quitarFila(i: number) {
    if (!edicion) return;
    const f = edicion.filas[i];
    const quitar = () => setEdicion(prev => (prev ? { ...prev, filas: prev.filas.filter((_, j) => j !== i) } : prev));
    if (!f.id) { quitar(); return; }
    if (!puedeBorrar) return;
    const filaId = f.id;
    setConfirmar({
      titulo: 'Borrar fila de carga de fuego',
      mensaje: `Se borrará la fila "${f.material || 'sin nombre'}" de este sector.`,
      accion: async () => { await borrarFilaCarga(filaId); quitar(); },
    });
  }

  // PE-A4: enlaces del sector con el inventario.
  async function recargarEnlaces() {
    try { setEnlaces(await cargarEnlaces(sectores.map(s => s.sector.id).filter((x): x is string => !!x))); setErrorInventario(''); }
    catch (err) { console.error('cargarEnlaces', err); setErrorInventario(msg(err)); }
  }
  async function enlazarUI(sectorId: string, warehouseId: string, zoneId: string | null) {
    setError(''); setOk('');
    await enlazarSector(companyId, { sector_id: sectorId, warehouse_id: warehouseId, zone_id: zoneId }, usuarioId);
    await recargarEnlaces();
    setOk('Sector enlazado.');
  }
  function pedirQuitarEnlace(e: EnlaceSector) {
    if (!e.id) return;
    const id = e.id;
    setConfirmar({
      titulo: 'Quitar enlace',
      mensaje: 'El sector deja de estar enlazado con esa bodega o zona. Las filas de carga de fuego que ya tiene no cambian.',
      accion: async () => { await quitarEnlace(id); await recargarEnlaces(); setOk('Enlace quitado.'); },
    });
  }
  function agregarPropuestas(ps: PropuestaCarga[]) {
    // La fila en blanco con la que nace un sector nuevo (sin id, sin kg, sin producto ni datos manuales) se reemplaza.
    const enBlanco = (f: FilaBorrador) => !f.id && !f.productId && !f.kg.trim() && !f.material.trim() && !f.qi.trim();
    setEdicion(b => (b ? { ...b, filas: b.filas.filter(f => !enBlanco(f)).concat(ps.map(propuestaABorrador)) } : b));
    setOk(`${plural(ps.length, 'fila agregada', 'filas agregadas')} al borrador del sector: revisa y guarda el sector para confirmar${ps.length === 1 ? 'la' : 'las'}.`);
  }

  // PE-A4: continuidad y póliza (M37), con su propio botón.
  async function guardarContinuidadUI(c: Continuidad, t: Transferencia): Promise<boolean> {
    if (!plan) return false;
    setError(''); setOk(''); setGuardando(true);
    try {
      await guardarContinuidadYTransferencia(plan, c, t);
      setPlan(await cargarPlan(companyId));
      setOk('Continuidad y póliza guardadas.');
      return true;
    } catch (err) {
      console.error('guardarContinuidad', err);
      setError(msg(err));
      return false;
    } finally {
      setGuardando(false);
    }
  }

  async function recargarEvidencias(planId: string) {
    try { setEvidencias(await cargarEvidencias(planId)); setErrorEvidencias(''); } catch (err) {
      console.error('cargarEvidencias', err);
      setEvidencias([]); setErrorEvidencias(msg(err));
    }
  }

  async function subirEvidenciaUI(f: FilaAccion, archivo: File) {
    setError(''); setOk('');
    await subirEvidencia(f, archivo, usuarioId);
    await recargarEvidencias(f.plan_id);
    syncPlanEmergenciasCompliance(companyId).catch(() => {}); // PO-18 cuenta la evidencia
    setOk('Evidencia adjuntada.');
  }

  function pedirBorrarEvidencia(e: FilaEvidencia) {
    setConfirmar({
      titulo: 'Borrar evidencia',
      mensaje: `Se borrará la evidencia "${e.file_name}". Si la acción es obligatoria y está cerrada, para PO-18 volverá a contar como abierta.`,
      accion: async () => {
        await borrarEvidencia(e);
        await recargarEvidencias(e.plan_id);
        syncPlanEmergenciasCompliance(companyId).catch(() => {});
        setOk('Evidencia borrada.');
      },
    });
  }

  async function abrirEvidencia(e: FilaEvidencia) {
    const url = await urlEvidencia(e);
    const a = document.createElement('a'); a.href = url; a.target = '_blank'; a.rel = 'noopener'; document.body.appendChild(a); a.click(); a.remove();
  }

  async function recargarAcciones(planId: string) {
    try { setAcciones(await cargarAcciones(planId)); setErrorAcciones(''); } catch (err) {
      console.error('cargarAcciones', err);
      setAcciones([]); setErrorAcciones(msg(err));
    }
  }

  // ── Pasos 3 y 4 ──
  const contexto = useMemo<ContextoPaso3>(() => ({
    cantonCode: plan?.canton_code ?? null, ocupacion: plan?.site_occupancy ?? null, areaM2: plan?.site_area_m2 ?? null,
    trabajadores: plan?.site_workers ?? null, zona: plan?.seismic_zone ?? null, zonaFuente: plan?.seismic_zone_source ?? null,
    lugar: plan?.seismic_place ?? null, bomberosKm: plan?.fire_station_km ?? null, bomberosMin: plan?.fire_station_min ?? null,
    sectores, brigadaMiembros, brigada: brigadaFilas,
    edificacion: plan?.site_is_new === true ? 'nueva' : plan?.site_is_new === false ? 'existente' : null,
  }), [plan, sectores, brigadaMiembros, brigadaFilas]);
  const evaluaciones = useMemo(() => calcularPaso3(entradas3, contexto), [entradas3, contexto]);
  const cambiadas = useMemo(() => resultadosCambiados(evaluaciones, filasEval), [evaluaciones, filasEval]);
  const derivadas = useMemo(() => evaluaciones.flatMap(ev => ev.acciones), [evaluaciones]);
  // PE-B2: lo que el paso 3 calcula para la evacuación (Togawa y RSET), para comparar con los simulacros.
  const planEvacuacion = useMemo((): PlanEvacuacion | null => {
    const r = evaluaciones.find(ev => ev.clave === 'rset')?.resumen;
    if (!r || typeof r.tsSegundos !== 'number') return null;
    const n = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);
    return {
      tsSegundos: r.tsSegundos, rsetSegundos: n(r.rsetSegundos), deteccionS: n(entradas3.evacuacion.deteccionS), alarmaS: n(entradas3.evacuacion.alarmaS),
      personas: n(r.n), anchoM: n(entradas3.evacuacion.anchoM), distanciaM: n(entradas3.evacuacion.distanciaM),
    };
  }, [evaluaciones, entradas3.evacuacion]);

  async function guardarAccionUI(a: AccionEditable): Promise<boolean> {
    if (!plan) return false;
    setError(''); setOk(''); setGuardando(true);
    try {
      await guardarAccion(companyId, plan.id, a, usuarioId);
      await recargarAcciones(plan.id);
      syncPlanEmergenciasCompliance(companyId).catch(() => {}); // PO-18 (PE-39)
      setOk('Acción guardada.');
      return true;
    } catch (err) {
      console.error('guardarAccion', err);
      setError(msg(err));
      return false;
    } finally {
      setGuardando(false);
    }
  }

  function pedirBorrarAccion(f: FilaAccion) {
    setConfirmar({
      titulo: 'Borrar acción',
      mensaje: `Se borrará la acción "${f.description.slice(0, 120)}"${f.is_mandatory ? '. Es obligatoria: si su origen sigue fallando, volverá a aparecer como acción por guardar' : ''}.`,
      accion: async () => {
        await borrarAccion(f.id);
        if (plan) await recargarAcciones(plan.id);
        syncPlanEmergenciasCompliance(companyId).catch(() => {});
        setOk('Acción borrada.');
      },
    });
  }

  async function guardarCalculos() {
    if (!plan) return;
    setError(''); setOk(''); setGuardando(true);
    try {
      await guardarPaso3(companyId, plan.id, evaluaciones, entradas3, usuarioId);
      // Solo después de un guardado correcto: limpiar el MESERI de grupos anteriores (admin; con manager, nada).
      try {
        await limpiarMeseriHuerfanas(plan.id, await cargarEvaluaciones(plan.id), evaluaciones, puedeBorrar);
      } catch (err) {
        console.error('limpiarMeseriHuerfanas', err);
      }
      setFilasEval(await cargarEvaluaciones(plan.id));
      // Plan de acción: marcar "origen resuelto" lo que ya no falla y reabrir lo que volvió a fallar (no se borra nada).
      try {
        const sinc = sincronizarAcciones(acciones, derivadas);
        await marcarOrigenResuelto(sinc.resolver, true);
        await marcarOrigenResuelto(sinc.reabrir, false);
        await recargarAcciones(plan.id);
        setOk(`Cálculos guardados (entradas, resultados y trazas).${sinc.propuestas.length ? ` Hay ${plural(sinc.propuestas.length, 'acción obligatoria', 'acciones obligatorias')} por guardar en ${TITULO_REVISION} › Plan de acción.` : ''}`);
      } catch (err) {
        console.error('sincronizar acciones', err);
        setOk('Cálculos guardados (entradas, resultados y trazas).');
        setError(`El plan de acción no se pudo actualizar: ${msg(err)}`);
      }
    } catch (e) {
      console.error('guardarCalculos', e);
      setError(msg(e));
    } finally {
      setGuardando(false);
    }
  }

  function pedirBorrarEvaluacion(f: FilaEvaluacion, titulo: string) {
    setConfirmar({
      titulo: 'Borrar evaluación guardada',
      mensaje: `Se borrará lo guardado de "${titulo}" (entradas, resultado y trazas). Lo que ves en pantalla se puede volver a guardar.`,
      accion: async () => {
        await borrarEvaluacion(f.id);
        if (plan) setFilasEval(await cargarEvaluaciones(plan.id));
        setOk(`"${titulo}": evaluación borrada.`);
      },
    });
  }

  // Borrar el plan (solo admin): modal propio que obliga a escribir el código. Los hijos se borran en cascada.
  // PE-A4b: con versiones aprobadas el modal lo dice desde el inicio y no deja confirmar (M36 tampoco lo permite).
  async function pedirBorrarPlan() {
    if (!plan || !puedeBorrar) return;
    const p = plan;
    let versiones = 0;
    try { versiones = await contarVersionesPlan(p.id); } catch (e) { setError(msg(e)); return; }
    setConfirmar({
      titulo: 'Borrar plan',
      mensaje: versiones > 0
        ? `"${p.code ?? 'Plan de emergencias'}" tiene ${plural(versiones, 'versión aprobada', 'versiones aprobadas')}: ${versiones === 1 ? 'queda como historia del plan y no se borra' : 'quedan como historia del plan y no se borran'}. Para cambiar el plan, edítalo y aprueba una versión nueva.`
        : `Se borrará el plan${p.code ? ` "${p.code}"` : ''} con sus sectores, carga de fuego, cálculos y plan de acción. Esta acción no se puede deshacer.`,
      bloqueado: versiones > 0 ? MENSAJE_PLAN_CON_VERSIONES : undefined,
      escribir: textoParaBorrarPlan(p.code),
      textoConfirmar: 'Borrar plan',
      accion: async () => {
        await borrarPlan(p);
        logAudit({ company_id: companyId, action: 'deleted', entity_type: 'emergency_plan', entity_id: p.id, entity_name: p.code ?? 'Plan de emergencias',
          details: { code: p.code, current_version: p.current_version } });
        setDestino({ vista: 1 });
        setOk('Plan borrado.');
        await cargar();
      },
    });
  }

  async function ejecutarConfirmacion() {
    if (!confirmar) return;
    setBorrando(true); setError('');
    try { await confirmar.accion(); setConfirmar(null); } catch (e) { console.error('confirmacion', e); setError(msg(e)); setConfirmar(null); } finally { setBorrando(false); }
  }

  // ── Navegación por capítulos (PE-C1) ──
  useEffect(() => { setDestino(vistaGuardada(companyId)); }, [companyId]);
  useEffect(() => {
    try { if (companyId) sessionStorage.setItem(CLAVE_VISTA(companyId), JSON.stringify({ vista, sub: subRev })); } catch { /* sin sessionStorage */ }
  }, [companyId, vista, subRev]);

  // Datos generales escritos y todavía sin guardar (comparados con lo guardado; los sugeridos también cuentan).
  const formulario1 = JSON.stringify([codigo, canton?.codigo ?? null, area, ocupacion, trabajadores, zona, zona ? zonaFuente : null, zona ? lugar : null,
    km, minutos, edificacion]);
  const guardado1 = useMemo(() => {
    const p = plan;
    const c = cantonPorCodigo(p?.canton_code) ?? cantonPorNombre(p?.site_canton);
    return JSON.stringify([p?.code ?? '', c?.codigo ?? null, aTexto(p?.site_area_m2), p?.site_occupancy ?? '', p?.site_workers != null ? String(p.site_workers) : '',
      p?.seismic_zone ?? '', p?.seismic_zone ? p.seismic_zone_source ?? 'manual' : null, p?.seismic_zone ? p.seismic_place ?? null : null,
      aTexto(p?.fire_station_km), aTexto(p?.fire_station_min), p?.site_is_new === true ? 'nueva' : p?.site_is_new === false ? 'existente' : '']);
  }, [plan]);
  const sucio1 = puedeEditar && formulario1 !== guardado1;

  function irA(d: Destino) {
    if (edicion) { setError('Guarda o cancela el sector que estás editando antes de cambiar de capítulo.'); return; }
    setOk(''); setError(''); setNavAbierta(false);
    setDestino({ vista: d.vista, sub: d.sub ?? (d.vista === 'revision' ? subRev : undefined) });
    window.scrollTo?.({ top: 0 });
  }
  // Las trazas y "Ver el plan de acción" dicen el paso de antes: se lleva al capítulo donde se completa.
  function irAPaso(n: number, claveEvaluacion?: string) { irA(vistaDePaso(n, claveEvaluacion)); }
  const destinoPaso = (n: number, claveEvaluacion?: string) => { const d = vistaDePaso(n, claveEvaluacion); return etiquetaVista(d.vista, d.sub); };
  function irASub(id: string) {
    document.querySelector(`[data-sub="${id}"]`)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    setNavAbierta(false);
  }

  const indice = ORDEN.indexOf(vista);
  async function siguiente() {
    // Como el "Guardar y seguir" de antes: desde los datos generales se guarda (si hay algo que guardar o aún no hay plan).
    if (vista === 1 && puedeEditar && (sucio1 || !plan) && !(await guardarPaso1())) return;
    irA({ vista: ORDEN[Math.min(ORDEN.length - 1, indice + 1)] });
  }

  if (!companyId) return null;
  const ro = !puedeEditar;
  const empresa = (impersonated ?? company)?.name ?? '';

  // ── Avance y vista previa (PE-C1) ──
  // El avance usa lo mismo que el modal de aprobación: plan y sectores guardados, cálculos y acciones de la pantalla.
  const progreso = resumenProgreso({ plan, sectores, evaluaciones, acciones });
  const numeroVivo = (t: string) => { const v = leerDecimalEc(t); return v === null || Number.isNaN(v) ? null : v; };
  // La vista previa muestra lo que se está escribiendo (datos generales y el sector en edición), aunque no esté guardado.
  const planVivo: FilaPlan | null = plan ? {
    ...plan, code: codigo.trim() || null, canton_code: canton?.codigo ?? null, site_canton: canton?.canton ?? null, site_province: canton?.provincia ?? null,
    site_area_m2: numeroVivo(area), site_occupancy: ocupacion || null, site_workers: numeroVivo(trabajadores),
  } : null;
  const datosVista: DatosVista = { empresa, plan: planVivo, sectores: enVivo, evaluaciones, acciones, brigadaMiembros };
  const faltanAqui = typeof vista === 'number' ? progreso.faltantes[vista] : [];

  const calculos = (bloques: string[], conGuardar: boolean) => plan && (
    <Paso3Calculos contexto={contexto} entradas={entradas3} setEntradas={setEntradas3} evaluaciones={evaluaciones}
      filas={filasEval} cambiadas={cambiadas} ro={ro} puedeBorrar={puedeBorrar} guardando={guardando}
      onGuardar={guardarCalculos} onBorrarEvaluacion={pedirBorrarEvaluacion} irAPaso={irAPaso} destino={destinoPaso}
      bloques={bloques} sinGuardar={!conGuardar} sinNota />
  );
  const sinPlan = <div className="pe2-card">{SIN_PLAN(ro)}</div>;
  const nombreTrabajador = (id: string) => trabajadoresLista.find(t => t.id === id)?.full_name ?? 'Trabajador';
  const titulo = typeof vista === 'number' ? `Capítulo ${vista} de 8 · ${capitulo(vista).titulo}`
    : vista === 'continuidad' ? `${TITULO_CONTINUIDAD_PESTANA} (opcional)` : TITULO_REVISION;

  return (
    <div className="pe2 pe2-capitulos">
      <div>
        <div className="pe2-paso">{titulo}</div>
        <h2>Plan de Emergencias v2</h2>
        <p className="pe2-sub">
          {plan?.code ? `Código ${plan.code} · ` : ''}
          {vigente ? estadoDelPlan(vigente, plan ? { plan, sectores, entradas: entradas3, acciones } : null) : 'Versión vigente: ninguna aprobada · Borrador'}
          {ro && ' · Solo lectura'}
        </p>
      </div>

      {error && <div className="pe2-error" role="alert">{error}</div>}
      {ok && <div className="pe2-ok">{ok}</div>}

      <div className="pe2-cap-layout">
        <nav className={`pe2-cap-nav${navAbierta ? ' abierta' : ''}`} aria-label="Capítulos del plan">
          <div className="pe2-progreso">
            <b>{progreso.texto}</b>
            <div className="pe2-progreso-barra" role="progressbar" aria-label="Avance del plan" aria-valuemin={0} aria-valuemax={progreso.total}
              aria-valuenow={progreso.completos.length} aria-valuetext={progreso.texto}>
              <span style={{ width: `${(progreso.completos.length / progreso.total) * 100}%` }} />
            </div>
          </div>
          <button type="button" className="pe2-btn pe2-cap-nav-boton" aria-expanded={navAbierta} onClick={() => setNavAbierta(a => !a)}>
            {navAbierta ? 'Ocultar capítulos' : 'Ver capítulos'}
          </button>
          <ol className="pe2-cap-lista">
            {CAPITULOS.map(c => {
              const sinReq = !tieneRequisitos(c.numero);
              const hecho = !sinReq && progreso.completos.includes(c.numero);
              return (
                <li key={c.numero}>
                  <button type="button" className={`pe2-cap-item${vista === c.numero ? ' on' : ''}`} aria-current={vista === c.numero ? 'step' : undefined}
                    aria-expanded={vista === c.numero} onClick={() => irA({ vista: c.numero })}
                    title={sinReq ? `${SIN_REQUISITOS}: todavía no tiene requisitos para aprobar el plan` : hecho ? 'Completo: tiene lo que pide la aprobación' : 'Le falta algo para aprobar'}>
                    <span className={`pe2-cap-estado${hecho ? ' hecho' : ''}${sinReq ? ' neutro' : ''}`} aria-hidden="true">{sinReq ? '–' : hecho ? '✓' : '○'}</span>
                    <span>{c.numero}. {c.titulo}</span>
                    <span className="pe2-sr">{sinReq ? ` (${SIN_REQUISITOS.toLowerCase()})` : hecho ? ' (completo)' : ' (incompleto)'}</span>
                  </button>
                  {vista === c.numero && (
                    <ul className="pe2-cap-subs">
                      {c.subapartados.map((s, i) => (
                        <li key={s.id}><button type="button" className="pe2-enlace" onClick={() => irASub(s.id)}>
                          {c.numero}.{i + 1} {s.titulo}{s.proximamente ? ` · ${PROXIMAMENTE}` : ''}</button></li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
          <div className="pe2-cap-extra">
            <button type="button" className={`pe2-cap-item${vista === 'continuidad' ? ' on' : ''}`} aria-current={vista === 'continuidad' ? 'step' : undefined}
              onClick={() => irA({ vista: 'continuidad' })}>
              <span className="pe2-cap-estado" aria-hidden="true">+</span><span>{TITULO_CONTINUIDAD_PESTANA} (opcional)</span>
            </button>
            <button type="button" className={`pe2-cap-item${vista === 'revision' ? ' on' : ''}`} aria-current={vista === 'revision' ? 'step' : undefined}
              onClick={() => irA({ vista: 'revision' })}>
              <span className="pe2-cap-estado" aria-hidden="true">★</span><span>{TITULO_REVISION}</span>
            </button>
          </div>
        </nav>

        <div className="pe2-cap-contenido">
          {typeof vista === 'number' && (
            !tieneRequisitos(vista)
              ? <div className="pe2-cap-neutro" role="status" data-testid="pe2-cap-estado">
                  {SIN_REQUISITOS}: este capítulo todavía no tiene requisitos para aprobar el plan.
                </div>
              : <div className={faltanAqui.length ? 'pe2-aviso' : 'pe2-cap-ok'} role="status" data-testid="pe2-cap-estado">
                  {faltanAqui.length
                    ? <>Para aprobar, en este capítulo falta:<ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>{faltanAqui.map((f, i) => <li key={i}>{textoConCapitulos(f)}</li>)}</ul></>
                    : 'Completo: tiene lo mínimo que pide la aprobación del plan.'}
                </div>
          )}

          {cargando ? <div className="pe2-card">Cargando…</div> : (
            <>
              {/* ── Capítulo 1: datos generales del establecimiento (antes, paso 1) ── */}
              {vista === 1 && (
                <>
                  <Sub {...subDe(1, 'informacion-general')}>
                    {sedePlan && (
                      <div className="pe2-sede" data-testid="pe2-sede">
                        <span>Sede: <b>{sedePlan.name}</b></span>
                        {avisoCanton?.tipo === 'copiar' && (
                          <span>
                            {' '}· La sede tiene cantón ({cantonPorCodigo(avisoCanton.canton)?.canton ?? avisoCanton.canton}) y este capítulo no.{' '}
                            {!ro && cantonPorCodigo(avisoCanton.canton) && (
                              <button type="button" className="pe2-btn" onClick={() => elegirCanton(cantonPorCodigo(avisoCanton.canton) as CantonInec)}>
                                Usar el cantón de la sede
                              </button>
                            )}
                          </span>
                        )}
                        {avisoCanton?.tipo === 'difiere' && (
                          <span role="status">
                            {' '}· El cantón de la sede ({cantonPorCodigo(avisoCanton.canton)?.canton ?? avisoCanton.canton}) no es el de este capítulo
                            ({canton?.canton}). El plan usa el de este capítulo.
                          </span>
                        )}
                      </div>
                    )}
                    <div className="pe2-grid">
                      <Campo etiqueta="Código del plan" nota="Lo define la empresa (por ejemplo PE-001).">
                        <input type="text" value={codigo} disabled={ro} onChange={e => setCodigo(e.target.value)} />
                      </Campo>
                      <Campo etiqueta="Versión" nota="Automática: sube al aprobar una versión nueva.">
                        <input type="text" disabled readOnly value={`${(plan?.current_version ?? 0) + 1} (borrador)`} />
                      </Campo>
                      <div className="pe2-campo">
                        <span>Cantón</span>
                        {canton && (
                          <div className="pe2-elegido">
                            <b>{canton.canton}</b> ({canton.provincia}) · INEC {canton.codigo}
                          </div>
                        )}
                        {!ro && (
                          <SelectBuscable ariaLabel="Buscar cantón" obligatorio vacio={canton ? 'Buscar otro cantón…' : 'Escribe el cantón o la provincia…'}
                            placeholder={canton ? 'Buscar otro cantón…' : 'Escribe el cantón o la provincia…'} value={canton?.codigo ?? ''}
                            opciones={CANTONES_INEC.map(c => ({ value: c.codigo, label: `${c.canton} (${c.provincia})` }))}
                            onChange={v => { const c = cantonPorCodigo(v); if (c) elegirCanton(c); }} />
                        )}
                        <small>{capasNormativas(canton?.codigo ?? null)} Lista: INEC, Clasificador Geográfico Estadístico 2026 (222 cantones).</small>
                      </div>
                      <Campo etiqueta="Ocupación o actividad principal">
                        <select value={ocupacion} disabled={ro} onChange={e => setOcupacion(e.target.value as Ocupacion)}>
                          <option value="">Elige…</option>
                          {OCUPACIONES.map(o => <option key={o} value={o}>{ETIQUETA_OCUPACION[o]}</option>)}
                        </select>
                      </Campo>
                      <Campo etiqueta="Edificación nueva o existente" nota="Decide la Tabla 3 (nueva) o 4 (existente) de la RTQ 5 para el ancho mínimo de escaleras.">
                        <select value={edificacion} disabled={ro} onChange={e => setEdificacion(e.target.value as Edificacion | '')}>
                          <option value="">Elige…</option>
                          <option value="nueva">Nueva</option>
                          <option value="existente">Existente</option>
                        </select>
                      </Campo>
                    </div>
                  </Sub>
                  <Sub {...subDe(1, 'instalaciones')}>
                    <div className="pe2-grid">
                      <Campo etiqueta="Área total">
                        <Numero valor={area} onChange={setArea} unidad="m²" disabled={ro} />
                      </Campo>
                    </div>
                    <div className="pe2-proximamente" role="note"><b>{PROXIMAMENTE}.</b> Linderos (norte, sur, este y oeste) y fotos por área. Las superficies por sector están en el capítulo 2.</div>
                  </Sub>
                  <Sub {...subDe(1, 'poblacion')}>
                    <div className="pe2-grid">
                      <Campo etiqueta="Número de trabajadores"
                        nota={sugeridos != null ? `Sugerido: ${formatoEc(sugeridos, 0)} trabajadores activos en el módulo Trabajadores.` : undefined}>
                        <Numero valor={trabajadores} onChange={setTrabajadores} unidad="personas" disabled={ro} entero />
                      </Campo>
                    </div>
                    <div className="pe2-proximamente" role="note"><b>{PROXIMAMENTE}.</b> Horario, turnos y visitantes. Los ocupantes por sector están en el capítulo 2.</div>
                  </Sub>
                  <Sub {...subDe(1, 'situacion')}>
                    <div className="pe2-grid">
                      {poblaciones.length > 0 && (
                        <Campo etiqueta="Población de la Tabla 19 (NEC-SE-DS)"
                          nota={elegida && elegida === poblaciones[0] && poblaciones.length > 1 ? AVISO_MAS_EXIGENTE : undefined}>
                          <SelectBuscable obligatorio disabled={ro} vacio="Zona ajustada a mano: elige una población para volver a la NEC…"
                            value={elegida ? etiquetaPoblacion(elegida) : ''} onChange={elegirPoblacion}
                            opciones={poblaciones.map(p => ({ value: etiquetaPoblacion(p), label: `${p.poblacion} · ${p.parroquia} · Z ${String(p.z).replace('.', ',')} · zona ${p.zona}` }))} />
                        </Campo>
                      )}
                      <Campo etiqueta="Zona sísmica (NEC-SE-DS)"
                        nota={<>
                          {canton ? notaZona(nec, zona ? zonaFuente : null, zona || null, elegida) : 'Se llena sola al elegir el cantón.'}
                          {nec && puedeEditar && !puedeBorrar && ' El ajuste manual de la zona es solo del administrador.'}
                        </>}>
                        <select value={zona} disabled={!puedeCambiarZona} onChange={e => cambiarZona(e.target.value as ZonaSismica | '')}>
                          <option value="">Elige…</option>
                          {ZONAS_SISMICAS.map(z => <option key={z} value={z}>Zona {z}</option>)}
                        </select>
                      </Campo>
                      {nec && zona && zonaFuente === 'manual' && (
                        <div className="pe2-aviso">
                          Ajustaste la zona a mano, fuera de la Tabla 19 de la NEC. Quedará guardada como "manual" y el informe lo indicará.
                        </div>
                      )}
                      <Campo etiqueta="Distancia a bomberos" nota="MESERI usa el peor de los dos: distancia o tiempo.">
                        <Numero valor={km} onChange={setKm} unidad="km" disabled={ro} />
                      </Campo>
                      <Campo etiqueta="Tiempo de llegada de bomberos">
                        <Numero valor={minutos} onChange={setMinutos} unidad="min" disabled={ro} />
                      </Campo>
                    </div>
                  </Sub>
                  <Sub {...subDe(1, 'justificacion')} />
                  <Sub {...subDe(1, 'politica')} />
                  {(puedeEditar || (plan && puedeBorrar)) && (
                    <div className="pe2-acciones">
                      {sucio1 && <span className="pe2-nota" role="status">Hay datos generales sin guardar.</span>}
                      {plan && puedeBorrar && <button type="button" className="pe2-btn peligro" disabled={guardando} onClick={pedirBorrarPlan}>Borrar plan</button>}
                      {puedeEditar && (
                        <button type="button" className="pe2-btn primario" disabled={guardando || (!sucio1 && !!plan)} onClick={() => { guardarPaso1(); }}>
                          {guardando ? 'Guardando…' : 'Guardar datos generales'}
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}

              {/* ── Capítulo 2: sectores y carga de fuego (antes, paso 2), MESERI y amenazas (antes, paso 3) ── */}
              {vista === 2 && (!plan ? sinPlan : (
                <>
                  <Sub {...subDe(2, 'sectores')}>
                    <p className="pe2-nota" style={{ margin: 0 }}>
                      Qs = [Σ(qi · Gi · Ci) / A] · R (RD 164/2025, Anexo I 3.2.1). Los totales los calcula StorSafe; no se escriben a mano.
                    </p>
                    <p className="pe2-nota" style={{ margin: 0 }}>
                      Tablas 1.3.3 ({MATERIALES_RD164.length} materiales) y 1.3.5 ({ACTIVIDADES_RD164.length} actividades) del RD 164,
                      verificadas contra el BOE. Aquí se ofrecen los {MATERIALES_POR_MASA.length} materiales en MJ/kg; lo demás se escribe
                      como "manual" con su fuente.
                    </p>
                    {sectores.length === 0 && <p className="pe2-nota">Aún no hay sectores.</p>}
                    {sectores.map(s => (
                      edicion?.id && edicion.id === s.sector.id ? null : (
                        <div key={s.sector.id} className="pe2-card" style={{ background: '#0f172a' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                            <div>
                              <h3>{s.sector.name}</h3>
                              <p className="pe2-sub">
                                {ETIQUETA_OCUPACION[s.sector.occupancy]} · {formatoEc(s.sector.area_m2, 2)} m²{s.sector.occupants != null ? ` · ${s.sector.occupants} ocupantes` : ''}{s.sector.has_sprinklers ? ' · con rociadores' : ''} · R = {formatoEc(s.r.r, 2)}
                                {s.r.rOrigen === 'manual' ? ' (manual)' : ''} · {plural(s.filas.length, 'fila', 'filas')} ·{' '}
                                {s.sector.fire_separated ? 'con separación cortafuego' : `sin separación cortafuego${s.sector.group_key ? ` (grupo ${s.sector.group_key})` : ''}`}
                              </p>
                            </div>
                            <div className="pe2-acciones">
                              <button className="pe2-btn" disabled={!!edicion} onClick={() => setEdicion(pantallaABorrador(s))}>{ro ? 'Ver' : 'Editar'}</button>
                              {puedeBorrar && <button className="pe2-btn peligro" disabled={!!edicion || guardando} onClick={() => borrarSectorConfirmado(s)}>Borrar</button>}
                            </div>
                          </div>
                          <Resultado calculo={calcularSector(s)} />
                        </div>
                      )
                    ))}
                    {puedeEditar && !edicion && (
                      <div className="pe2-acciones">
                        <button className="pe2-btn primario" onClick={() => setEdicion({
                          sortOrder: sectores.length, nombre: '', area: '', ocupantes: '', rociadores: false, actividad: 'almacenamiento', cortafuego: true, grupo: '',
                          rSel: '1', rManual: '', rFuente: '', filas: [filaNueva()],
                        })}>Agregar sector</button>
                      </div>
                    )}
                    {edicion && (
                      <EditorSector b={edicion} set={setEdicion} ro={ro} puedeBorrar={puedeBorrar} guardando={guardando}
                        calculo={calculoEdicion} onGuardar={guardarEdicion} onCancelar={() => { setEdicion(null); setError(''); }} onQuitarFila={quitarFila}
                        inventario={
                          <Paso2Inventario sectorId={edicion.id} enlaces={enlaces.filter(e => e.sector_id === edicion.id)} bodegas={bodegas} error={errorInventario}
                            ro={ro} puedeBorrar={puedeBorrar} yaEnSector={new Set(edicion.filas.map(f => f.productId).filter((x): x is string => !!x))}
                            onEnlazar={(w, z) => enlazarUI(edicion.id as string, w, z)} onQuitarEnlace={pedirQuitarEnlace}
                            cargarInventario={es => cargarInventarioDe(companyId, bodegas, es)} onAgregar={agregarPropuestas} />
                        } />
                    )}
                    <div className="pe2-card" style={{ background: '#0f172a' }}>
                      <h3>Sectores sin separación cortafuego</h3>
                      <p className="pe2-nota" style={{ margin: 0 }}>
                        Se evalúan juntos, sumando áreas y materiales, con el R mayor (RD 164, Tabla 1.3.4).
                      </p>
                      {grupos.length === 0
                        ? <p className="pe2-nota">No hay grupos: cada sector se evalúa solo.</p>
                        : grupos.map(g => (
                          <div key={g.nombre} className="pe2-card" style={{ background: '#1e293b' }}>
                            <h3>Grupo: {g.miembros.join(' + ')}</h3>
                            <Resultado calculo={g.calculo} />
                            {g.trazas.map((t, i) => <p key={i} className="pe2-nota" style={{ margin: 0 }}>{t.nota}</p>)}
                          </div>
                        ))}
                    </div>
                  </Sub>
                  <Sub {...subDe(2, 'meseri')}>{calculos(['meseri'], false)}</Sub>
                  <Sub {...subDe(2, 'amenazas')}>{calculos(['amenaza_vulnerabilidad'], true)}</Sub>
                  <Sub {...subDe(2, 'acciones-preventivas')}>
                    <p className="pe2-nota" style={{ margin: 0 }}>
                      Las acciones que salen de los cálculos (incumplimientos, punto de encuentro no apto) se guardan con responsable y fecha en {TITULO_REVISION} › Plan de acción.
                      Hoy hay {plural(acciones.length, 'acción guardada', 'acciones guardadas')}.
                    </p>
                    <div className="pe2-acciones"><button type="button" className="pe2-btn" onClick={() => irA({ vista: 'revision', sub: 'acciones' })}>Ir al plan de acción</button></div>
                  </Sub>
                </>
              ))}

              {/* ── Capítulo 3: recursos y capacidades (verificación normativa del paso 3) ── */}
              {vista === 3 && (!plan ? sinPlan : (
                <>
                  <Sub {...subDe(3, 'verificacion')}>{calculos(['verificacion'], true)}</Sub>
                  <Sub {...subDe(3, 'equipos')} />
                </>
              ))}

              {/* ── Capítulo 4: organización (brigada) ── */}
              {vista === 4 && (!plan ? sinPlan : (
                <>
                  <Sub {...subDe(4, 'brigada')}>
                    <div className="pe2-res">
                      <div><b>{brigadaMiembros ?? '—'}</b><span>Miembros de la brigada</span></div>
                      <div><b>{brigadaFilas ? brigadaFilas.filter(b => b.trained).length : '—'}</b><span>Con capacitación registrada</span></div>
                      <div><b>911</b><span>Teléfono de emergencias</span></div>
                    </div>
                    <p className="pe2-nota" style={{ margin: 0 }}>
                      La brigada se registra hoy en la pantalla "Plan de Emergencias" (anterior). La usan MESERI, la verificación normativa y los simulacros.
                    </p>
                  </Sub>
                  <Sub {...subDe(4, 'responsabilidades')} />
                </>
              ))}

              {/* ── Capítulo 5: procedimientos de respuesta ── */}
              {vista === 5 && (!plan ? sinPlan : (
                <>
                  <Sub {...subDe(5, 'alarma')} />
                  <Sub {...subDe(5, 'grados')} />
                </>
              ))}

              {/* ── Capítulo 6: evacuación y punto de encuentro (paso 3) ── */}
              {vista === 6 && (!plan ? sinPlan : (
                <>
                  <Sub {...subDe(6, 'aforo')}>{calculos(['aforo_egreso'], false)}</Sub>
                  <Sub {...subDe(6, 'rset')}>{calculos(['rset'], false)}</Sub>
                  <Sub {...subDe(6, 'punto-encuentro')}>{calculos(['punto_encuentro'], true)}</Sub>
                </>
              ))}

              {/* ── Capítulo 7: simulacros (antes, pestaña Simulacros) y capacitación ── */}
              {vista === 7 && (!plan ? sinPlan : (
                <>
                  <Sub {...subDe(7, 'simulacros')}>
                    <Simulacros companyId={companyId} empresa={empresa} plan={plan}
                      sectores={sectores.flatMap(s => (s.sector.id ? [{ id: s.sector.id, name: s.sector.name, occupants: s.sector.occupants ?? null }] : []))}
                      planEvacuacion={planEvacuacion} amenazas={entradas3.amenazas.map(a => ({ clave: a.id, nombre: a.nombre }))}
                      trabajadores={trabajadoresLista} brigada={brigadaFilas ? brigadaFilas.map(b => b.workerId) : null} acciones={acciones}
                      usuarioId={usuarioId} puedeEditar={puedeEditar} puedeBorrar={puedeBorrar} quito={plan.canton_code === '1701'}
                      onGuardarAccion={guardarAccionUI} onCambio={() => { syncPlanEmergenciasCompliance(companyId).catch(() => {}); }} />
                  </Sub>
                  <Sub {...subDe(7, 'capacitacion')}>
                    {!brigadaFilas || brigadaFilas.length === 0 ? <p className="pe2-nota">No hay brigadistas registrados.</p> : (
                      <ul className="pe2-vista-lista" aria-label="Capacitación de la brigada">
                        {brigadaFilas.map(b => (
                          <li key={b.workerId}>{nombreTrabajador(b.workerId)} · {b.trained ? `capacitado${b.trainingDate ? ` el ${b.trainingDate}` : ''}` : 'sin capacitación registrada'}</li>
                        ))}
                      </ul>
                    )}
                    <div className="pe2-proximamente" role="note"><b>{PROXIMAMENTE}.</b> Programa de capacitación del plan.</div>
                  </Sub>
                </>
              ))}

              {/* ── Capítulo 8: anexos ── */}
              {vista === 8 && (!plan ? sinPlan : (
                <>
                  <Sub {...subDe(8, 'mapa')} />
                  <Sub {...subDe(8, 'cronograma')}>
                    <ul className="pe2-vista-lista">
                      {(armarVistaPrevia(datosVista).find(x => x.numero === '17')?.lineas ?? []).map((l, i) => (
                        <li key={i} className={l.pendiente ? 'pe2-pendiente' : undefined}>{l.texto}</li>
                      ))}
                    </ul>
                  </Sub>
                  <Sub {...subDe(8, 'pendientes')}>
                    {(() => {
                      const lista = pendientes(datosVista).filter(x => x.clave !== 'continuidad' || tieneContinuidad(plan));
                      return lista.length === 0 ? <p className="pe2-nota">No hay pendientes.</p> : (
                        <ul className="pe2-vista-lista" aria-label="Pendientes por validar">
                          {lista.map((x, i) => <li key={i}><b>{x.grupo}</b> · {x.articulo}: {x.falta}{dondeSeCompleta(x) ? ` · se completa en ${dondeSeCompleta(x)}` : ''}</li>)}
                        </ul>
                      );
                    })()}
                  </Sub>
                </>
              ))}

              {/* ── Continuidad del negocio y póliza (antes, en el paso 1) ── */}
              {vista === 'continuidad' && (
                <>
                  <div className="pe2-aviso" role="note">{AVISO_CONTINUIDAD}</div>
                  {plan ? <Paso1Continuidad plan={plan} ro={ro} guardando={guardando} onGuardar={guardarContinuidadUI} /> : sinPlan}
                </>
              )}

              {/* ── Revisión y aprobación (antes, pasos 4, 5 y 6) ── */}
              {vista === 'revision' && (!plan ? sinPlan : (
                <>
                  <div className="pe2-vistas" role="tablist" aria-label={TITULO_REVISION}>
                    {SUB_REVISION.map(s => (
                      <button key={s.id} type="button" role="tab" aria-selected={subRev === s.id} className={subRev === s.id ? 'on' : ''}
                        onClick={() => irA({ vista: 'revision', sub: s.id })}>{s.titulo}</button>
                    ))}
                  </div>
                  {subRev === 'resultados' && (
                    <Paso4Resultados evaluaciones={evaluaciones} sectores={sectores} irAPaso={irAPaso} destino={destinoPaso}
                      meseriAnteriores={meseriHuerfanas(filasEval, evaluaciones).length} />
                  )}
                  {subRev === 'acciones' && (errorAcciones
                    ? <div className="pe2-error" role="alert">No se pudo leer el plan de acción: {errorAcciones}</div>
                    : <Paso5Acciones acciones={acciones} derivadas={derivadas} trabajadores={trabajadoresLista} ro={ro} puedeBorrar={puedeBorrar}
                        guardando={guardando} onGuardar={guardarAccionUI} onBorrar={pedirBorrarAccion}
                        evidencias={evidencias} errorEvidencias={errorEvidencias} onSubirEvidencia={subirEvidenciaUI}
                        onBorrarEvidencia={pedirBorrarEvidencia} onAbrirEvidencia={abrirEvidencia} />)}
                  {subRev === 'aprobacion' && (
                    <Paso6Versiones plan={plan} entradas={entradas3} companyId={companyId} usuarioId={usuarioId}
                      usuarioNombre={profile?.full_name || profile?.email || null} puedeAprobar={puedeBorrar}
                      trabajadores={trabajadoresLista} onAprobada={async () => { await cargar(); syncPlanEmergenciasCompliance(companyId).catch(() => {}); }}
                      datos={{ empresa, plan, sectores, evaluaciones, acciones, brigadaMiembros }} textoFaltante={textoConCapitulos} />
                  )}
                </>
              ))}
            </>
          )}

          <div className="pe2-barra">
            <button className="pe2-btn" disabled={indice <= 0 || guardando} onClick={() => irA({ vista: ORDEN[Math.max(0, indice - 1)] })}>Anterior</button>
            <div className="centro">{typeof vista === 'number' ? `Capítulo ${vista} de 8` : vista === 'continuidad' ? 'Opcional' : TITULO_REVISION}</div>
            <button className="pe2-btn primario" disabled={indice >= ORDEN.length - 1 || guardando || cargando} onClick={siguiente}>
              {vista === 1 && puedeEditar && (sucio1 || !plan) ? (guardando ? 'Guardando…' : 'Guardar y seguir')
                : indice >= 7 ? (vista === 8 ? 'Continuidad (opcional)' : TITULO_REVISION) : 'Siguiente capítulo'}
            </button>
          </div>
        </div>

        {!cargando && <VistaPreviaCapitulos datos={datosVista} vista={vista} />}
      </div>

      <ModalConfirmar abierto={!!confirmar} titulo={confirmar?.titulo ?? ''} mensaje={confirmar?.mensaje ?? ''}
        escribir={confirmar?.escribir} textoConfirmar={confirmar?.textoConfirmar} bloqueado={confirmar?.bloqueado}
        ocupado={borrando} onConfirmar={ejecutarConfirmacion} onCancelar={() => setConfirmar(null)} />
    </div>
  );
}

// ── Editor de un sector con sus filas ────────────────────────────────────────

function EditorSector({ b, set, ro, puedeBorrar, guardando, calculo, onGuardar, onCancelar, onQuitarFila, inventario }: {
  b: SectorBorrador; set: (b: SectorBorrador) => void; ro: boolean; puedeBorrar: boolean; guardando: boolean;
  calculo: Calculo | null; onGuardar: () => void; onCancelar: () => void; onQuitarFila: (i: number) => void;
  inventario?: React.ReactNode;
}) {
  const fila = (i: number, cambio: Partial<FilaBorrador>) => set({ ...b, filas: b.filas.map((f, j) => (j === i ? { ...f, ...cambio } : f)) });
  return (
    <div className="pe2-card" style={{ borderColor: '#0284c7' }}>
      <h3>{b.id ? (ro ? 'Sector' : 'Editar sector') : 'Nuevo sector'}</h3>
      <div className="pe2-grid">
        <Campo etiqueta="Nombre"><input type="text" value={b.nombre} disabled={ro} onChange={e => set({ ...b, nombre: e.target.value })} /></Campo>
        <Campo etiqueta="Área del sector (A)"><Numero valor={b.area} onChange={v => set({ ...b, area: v })} unidad="m²" disabled={ro} /></Campo>
        <Campo etiqueta="Ocupantes del sector" nota="Opcional. Las escaleras de este sector se calculan con estos ocupantes (RTQ 5, 5.2.2); sin dato, con el aforo total del plan.">
          <Numero valor={b.ocupantes} onChange={v => set({ ...b, ocupantes: v })} unidad="personas" disabled={ro} entero />
        </Campo>
        <Campo etiqueta="Actividad">
          <select value={b.actividad} disabled={ro} onChange={e => set({ ...b, actividad: e.target.value as Ocupacion })}>
            {OCUPACIONES_SECTOR.map(o => <option key={o} value={o}>{ETIQUETA_OCUPACION[o]}</option>)}
          </select>
        </Campo>
        <Campo etiqueta="Coeficiente R" nota={FUENTE_R}>
          <select value={b.rSel} disabled={ro} onChange={e => set({ ...b, rSel: e.target.value })}>
            {OPCIONES_R.map(o => <option key={o.valor} value={String(o.valor)}>R = {formatoEc(o.valor, o.valor === 1 ? 0 : 1)}: {o.texto}</option>)}
            <option value="manual">Manual (con fuente)</option>
          </select>
        </Campo>
        {b.rSel === 'manual' && (
          <>
            <Campo etiqueta="R manual"><Numero valor={b.rManual} onChange={v => set({ ...b, rManual: v })} unidad="—" disabled={ro} /></Campo>
            <Campo etiqueta="Fuente de R (obligatoria)" nota="Fuera de la Tabla 1.3.4: el PDF lo mostrará con aviso.">
              <input type="text" value={b.rFuente} disabled={ro} onChange={e => set({ ...b, rFuente: e.target.value })} />
            </Campo>
          </>
        )}
      </div>
      <label className="pe2-check">
        <input type="checkbox" checked={b.cortafuego} disabled={ro} onChange={e => set({ ...b, cortafuego: e.target.checked })} />
        Tiene separación cortafuego
      </label>
      <label className="pe2-check">
        <input type="checkbox" checked={b.rociadores} disabled={ro} onChange={e => set({ ...b, rociadores: e.target.checked })} />
        Tiene rociadores automáticos (recorrido máximo de la RTQ 3)
      </label>
      {!b.cortafuego && (
        <Campo etiqueta="Grupo (opcional)" nota="Sin grupo, todos los sectores sin separación cortafuego se evalúan juntos.">
          <input type="text" value={b.grupo} disabled={ro} onChange={e => set({ ...b, grupo: e.target.value })} />
        </Campo>
      )}

      {inventario}

      <h3>Carga de fuego</h3>
      {b.filas.map((f, i) => {
        const mat = f.modo === 'tabla' ? materialPorClave(f.tableRef) : undefined;
        let ciTexto = '';
        try { ciTexto = formatoEc(ciPorGrado(f.ciGrado, f.ciHumo), 2); } catch { ciTexto = '—'; }
        return (
          <div key={i} className="pe2-fila">
            <Campo etiqueta="Material">
              <SelectBuscable obligatorio disabled={ro} value={f.modo === 'tabla' ? f.tableRef : 'manual'}
                opciones={[...MATERIALES_POR_MASA.map(m => ({ value: m.clave, label: m.nombre.replace(/\.$/, '') })), { value: 'manual', label: 'Manual (con fuente)' }]}
                onChange={v => (v === 'manual'
                  ? fila(i, { modo: 'manual', ciModo: f.ciModo === 'tabla_fila' ? 'grado' : f.ciModo })
                  : fila(i, { modo: 'tabla', tableRef: v, ciModo: 'tabla_fila', ciCompacto: false, ciJustificacion: '' }))} />
            </Campo>
            {f.modo === 'manual' ? (
              <>
                <Campo etiqueta="Nombre del material"><input type="text" value={f.material} disabled={ro} onChange={e => fila(i, { material: e.target.value })} /></Campo>
                <Campo etiqueta="qi"><Numero valor={f.qi} onChange={v => fila(i, { qi: v })} unidad="MJ/kg" disabled={ro} /></Campo>
              </>
            ) : (
              <>
                <Campo etiqueta="qi (tabla)"><input type="text" disabled readOnly value={mat ? `${formatoEc(mat.qi, 2)} MJ/kg` : '—'} /></Campo>
                <Campo etiqueta="Ci (tabla)">
                  <input type="text" disabled readOnly
                    value={mat ? formatoEc(f.ciCompacto && permiteCiCompacto(mat) ? CI_NOTA_COMPACTO : mat.ci, 2) : '—'} />
                </Campo>
              </>
            )}
            <Campo etiqueta="Masa (Gi)"><Numero valor={f.kg} onChange={v => fila(i, { kg: v })} unidad="kg" disabled={ro} /></Campo>
            <div>
              {!ro && (!f.id || puedeBorrar) && (
                <button className="pe2-btn peligro" aria-label="Quitar fila" onClick={() => onQuitarFila(i)}>Quitar</button>
              )}
            </div>
            {f.modo === 'manual' && (
              <>
                <div className="ancho">
                  <Campo etiqueta="Fuente de qi (obligatoria)" nota="Hoja de seguridad, ficha técnica… El PDF mostrará el aviso de valor fuera de tabla.">
                    <input type="text" value={f.qFuente} disabled={ro} onChange={e => fila(i, { qFuente: e.target.value })} />
                  </Campo>
                </div>
                <Campo etiqueta="Ci" nota={FUENTE_CI}>
                  <select value={f.ciModo} disabled={ro} onChange={e => fila(i, { ciModo: e.target.value as ModoCi })}>
                    <option value="grado">Tabla 1.3.2 (por grado)</option>
                    <option value="manual">Manual (con fuente)</option>
                  </select>
                </Campo>
                {f.ciModo === 'grado' ? (
                  <>
                    <Campo etiqueta="Grado de peligrosidad">
                      <select value={f.ciGrado} disabled={ro} onChange={e => fila(i, { ciGrado: Number(e.target.value) })}>
                        {[1, 2, 3, 4, 5].map(g => <option key={g} value={g}>Grado {g}</option>)}
                      </select>
                    </Campo>
                    <label className="pe2-check">
                      <input type="checkbox" checked={f.ciHumo} disabled={ro} onChange={e => fila(i, { ciHumo: e.target.checked })} /> Fumígeno
                    </label>
                    <Campo etiqueta="Ci resultante"><input type="text" disabled readOnly value={ciTexto} /></Campo>
                  </>
                ) : (
                  <>
                    <Campo etiqueta="Ci manual"><Numero valor={f.ci} onChange={v => fila(i, { ci: v })} unidad="—" disabled={ro} /></Campo>
                    <div className="ancho">
                      <Campo etiqueta="Fuente de Ci (obligatoria)">
                        <input type="text" value={f.ciFuente} disabled={ro} onChange={e => fila(i, { ciFuente: e.target.value })} />
                      </Campo>
                    </div>
                  </>
                )}
                <div className="pe2-fuente pe2-aviso">Valor manual: fuera de las tablas del RD 164.</div>
              </>
            )}
            {mat && permiteCiCompacto(mat) && (
              <div className="ancho">
                <label className="pe2-check">
                  <input type="checkbox" checked={f.ciCompacto} disabled={ro} onChange={e => fila(i, { ciCompacto: e.target.checked })} />
                  Usar Ci = 1,00 (nota {mat.ciNota})
                </label>
                {f.ciCompacto && (
                  <Campo etiqueta="Justificación (obligatoria)" nota={NOTAS_T133[mat.ciNota as number]}>
                    <input type="text" value={f.ciJustificacion} disabled={ro} onChange={e => fila(i, { ciJustificacion: e.target.value })} />
                  </Campo>
                )}
              </div>
            )}
            {f.origen && (
              <div className="pe2-fuente">
                {f.origen}
                {!f.kg.trim() && <> · <span className="pe2-resaltado">por validar: falta la masa en kg</span></>}
              </div>
            )}
            {mat && (
              <div className="pe2-fuente">
                Fuente: RD 164/2025, Tabla 1.3.3 · {mat.fuente_doc} · verificado contra el BOE.
                {mat.ciNota === 4 && ` ${NOTAS_T133[4]}`}
              </div>
            )}
          </div>
        );
      })}
      {!ro && <div><button className="pe2-btn" onClick={() => set({ ...b, filas: b.filas.concat(filaNueva()) })}>Agregar fila</button></div>}

      <h3>Resultado en vivo</h3>
      {calculo && <Resultado calculo={calculo} />}

      <div className="pe2-acciones">
        <button className="pe2-btn" onClick={onCancelar} disabled={guardando}>{ro ? 'Cerrar' : 'Cancelar'}</button>
        {!ro && <button className="pe2-btn primario" onClick={onGuardar} disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar sector'}</button>}
      </div>
    </div>
  );
}
